import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions';
import AdminLayout from '../../layouts/AdminLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import notify from '../../utils/IANotification';
import Switch from 'react-switch';
import { MdArrowBack, MdSave, MdCheck, MdClose, MdRepeat, MdEmail, MdInfo, MdPerson } from 'react-icons/md';
import { fetch_booking_by_id, create_booking, approve_booking, reject_booking, cancel_booking, fetch_all_locations } from '../../api/locationsAPI';

export default function FacilityBookingFormPage() {
    const navigate = useNavigate();
    useFetchAuthorisation("user");
    const userData = useFetchUserData();

    const booking_id = new URLSearchParams(window.location.search).get("booking_id") || "new";
    const initialLocationId = new URLSearchParams(window.location.search).get("location_id") || "";

    const [locations, setLocations] = useState([]);
    const [bookingData, setBookingData] = useState({
        location_id: initialLocationId,
        title: "",
        description: "",
        start_datetime: "",
        end_datetime: "",
        status: "pending",
        is_recurring: "0",
        recurrence_rule: "weekly_indefinite",
        recurrence_count: 10,
    });

    const loadLocations = async () => {
        const res = await fetch_all_locations(true); // Only fetch booking enabled locations
        if (res?.status === 200) {
            setLocations(res.data || []);
            if (!initialLocationId && res.data?.length > 0 && booking_id === "new") {
                setBookingData(prev => ({ ...prev, location_id: res.data[0].id }));
            }
        }
    };

    const loadBookingData = async () => {
        if (booking_id === "new") return;
        const res = await fetch_booking_by_id(booking_id);
        if (res?.status === 200 && res.data) {
            const b = res.data;
            setBookingData({
                ...b,
                start_datetime: b.start_datetime ? new Date(b.start_datetime).toISOString().slice(0, 16) : "",
                end_datetime: b.end_datetime ? new Date(b.end_datetime).toISOString().slice(0, 16) : "",
            });
        }
    };

    useEffect(() => {
        loadLocations();
        loadBookingData();
    }, [booking_id]);

    const handleSave = async () => {
        if (!bookingData.location_id || !bookingData.title || !bookingData.start_datetime || !bookingData.end_datetime) {
            return notify({ type: "error", message: "Please fill in all required fields (location, title, start & end time)" });
        }

        if (booking_id === "new") {
            const payload = {
                ...bookingData,
                user_id: userData?.id,
            };
            const res = await create_booking(payload);
            if (res?.status === 200) {
                notify({ type: "success", message: res.message || "Booking created successfully" });
                navigate('/admin/locations/bookings');
            } else {
                notify({ type: "error", message: res?.message || "Failed to submit booking" });
            }
        }
    };

    const handleApprove = async () => {
        const res = await approve_booking(booking_id);
        if (res?.status === 200) {
            notify({ type: "success", message: res.message || "Booking approved successfully" });
            loadBookingData();
        } else {
            notify({ type: "error", message: res?.message || "Failed to approve booking" });
        }
    };

    const handleReject = async () => {
        const reason = window.prompt("Enter rejection reason (optional):");
        if (reason === null) return;
        const res = await reject_booking(booking_id, reason);
        if (res?.status === 200) {
            notify({ type: "success", message: res.message || "Booking rejected" });
            loadBookingData();
        } else {
            notify({ type: "error", message: res?.message || "Failed to reject booking" });
        }
    };

    const handleCancel = async () => {
        const res = await cancel_booking(booking_id);
        if (res?.status === 200) {
            notify({ type: "success", message: "Booking cancelled" });
            loadBookingData();
        } else {
            notify({ type: "error", message: res?.message || "Failed to cancel booking" });
        }
    };

    return (
        <AdminLayout
            navSelected={"Facility Bookings"}
            headerText={booking_id === "new" ? "Create Booking" : "Booking Details"}
            toolbar={
                <>
                    <div className="flex items-center gap-3">
                        <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/locations/bookings')}>
                            Back
                        </Button>
                        <h1 className="text-xl font-bold text-gray-900">
                            {booking_id === "new" ? "New Facility Booking" : bookingData.title || "Booking Details"}
                        </h1>
                    </div>
                    <div className="flex gap-2">
                        {booking_id !== "new" && bookingData.status === "pending" && (
                            <>
                                <Button variant="success" icon={MdCheck} onClick={handleApprove}>
                                    {bookingData.is_recurring === "1" ? "Approve All Instances" : "Approve"}
                                </Button>
                                <Button variant="danger" icon={MdClose} onClick={handleReject}>
                                    Reject
                                </Button>
                            </>
                        )}
                        {booking_id !== "new" && bookingData.status !== "cancelled" && (
                            <Button variant="secondary" onClick={handleCancel}>
                                Cancel Booking
                            </Button>
                        )}
                        {booking_id === "new" && (
                            <Button variant="primary" icon={MdSave} onClick={handleSave}>
                                Submit Booking
                            </Button>
                        )}
                    </div>
                </>
            }
        >
            <div className="space-y-6">
                {/* APPROVAL BANNER */}
                {booking_id !== "new" && bookingData.status === "pending" && (
                    <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200 flex flex-wrap items-center justify-between gap-4">
                        <div>
                            <span className="font-bold text-sm text-amber-900 flex items-center gap-1.5">
                                <MdInfo className="text-base text-amber-600" />
                                Booking Approval Required
                            </span>
                            <p className="text-xs text-amber-700 mt-0.5">
                                {bookingData.is_recurring === "1"
                                    ? `This is a recurring weekly booking (${bookingData.recurrence_rule === "weekly_count" ? `${bookingData.recurrence_count} occurrences` : "Forever / 52 weeks"}). Approving will approve ALL recurring instances.`
                                    : "Review requester details and booking purpose below before approving or rejecting."}
                            </p>
                        </div>

                        <div className="flex items-center gap-2">
                            <Button variant="success" icon={MdCheck} onClick={handleApprove}>
                                {bookingData.is_recurring === "1" ? "Approve All Recurring Instances" : "Approve Booking"}
                            </Button>
                            <Button variant="danger" icon={MdClose} onClick={handleReject}>
                                Reject Booking
                            </Button>
                        </div>
                    </div>
                )}

                {/* REQUESTER INFORMATION CARD */}
                {booking_id !== "new" && (
                    <Card title="Applicant & Requester Information" subtitle="Detailed information about the user requesting this facility booking.">
                        <div className="flex flex-wrap items-center justify-between gap-6 p-4 bg-gray-50 rounded-2xl border border-gray-200">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-lg">
                                    {bookingData.user_data?.first_name ? bookingData.user_data.first_name[0].toUpperCase() : "U"}
                                </div>
                                <div>
                                    <h3 className="font-bold text-base text-gray-900">
                                        {bookingData.user_data ? `${bookingData.user_data.first_name} ${bookingData.user_data.last_name}` : `User #${bookingData.user_id}`}
                                    </h3>
                                    <p className="text-xs text-gray-500 flex items-center gap-1">
                                        <MdEmail className="text-xs text-gray-400" />
                                        {bookingData.user_data?.email || "No email recorded"}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-4 text-xs">
                                <div className="bg-white px-3 py-2 rounded-xl border border-gray-200 shadow-sm">
                                    <span className="text-gray-400 text-[10px] uppercase font-bold block">User ID</span>
                                    <span className="font-semibold text-gray-800">#{bookingData.user_id}</span>
                                </div>
                                <div className="bg-white px-3 py-2 rounded-xl border border-gray-200 shadow-sm">
                                    <span className="text-gray-400 text-[10px] uppercase font-bold block">Account Type</span>
                                    <span className="font-semibold text-gray-800 capitalize">{bookingData.user_data?.account_type || "Member"}</span>
                                </div>
                            </div>
                        </div>
                    </Card>
                )}
            <Card title="Booking Information" subtitle="Select facility location, dates, times, and purpose.">
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="input-container">
                            <label className="input-label font-semibold">Target Location / Facility</label>
                            <select
                                className="select-input"
                                disabled={booking_id !== "new"}
                                value={bookingData.location_id || ""}
                                onChange={(e) => setBookingData({ ...bookingData, location_id: e.target.value })}
                            >
                                {locations.map(loc => (
                                    <option key={loc.id} value={loc.id}>
                                        {loc.name} ({loc.location_type} - Cap: {loc.capacity} pax)
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="input-container">
                            <label className="input-label font-semibold">Event / Booking Purpose</label>
                            <input
                                type="text"
                                className="text-input"
                                placeholder="e.g. Worship Team Rehearsal"
                                disabled={booking_id !== "new"}
                                value={bookingData.title || ""}
                                onChange={(e) => setBookingData({ ...bookingData, title: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="input-container">
                            <label className="input-label font-semibold">Start Date &amp; Time</label>
                            <input
                                type="datetime-local"
                                className="text-input"
                                disabled={booking_id !== "new"}
                                value={bookingData.start_datetime || ""}
                                onChange={(e) => setBookingData({ ...bookingData, start_datetime: e.target.value })}
                            />
                        </div>

                        <div className="input-container">
                            <label className="input-label font-semibold">End Date &amp; Time</label>
                            <input
                                type="datetime-local"
                                className="text-input"
                                disabled={booking_id !== "new"}
                                value={bookingData.end_datetime || ""}
                                onChange={(e) => setBookingData({ ...bookingData, end_datetime: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="input-container">
                        <label className="input-label font-semibold">Additional Details &amp; Equipment Needed</label>
                        <textarea
                            className="textarea-input h-28"
                            placeholder="e.g. Requires 2 wireless microphones, projector setup..."
                            disabled={booking_id !== "new"}
                            value={bookingData.description || ""}
                            onChange={(e) => setBookingData({ ...bookingData, description: e.target.value })}
                        />
                    </div>

                    {booking_id === "new" && (
                        <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100 space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <label className="font-semibold text-sm text-gray-900">Make Recurring Booking</label>
                                    <p className="text-xs text-gray-500">Automatically repeat this weekly booking on the same day and time.</p>
                                </div>
                                <Switch
                                    checked={bookingData.is_recurring === "1"}
                                    onChange={(checked) => setBookingData({ ...bookingData, is_recurring: checked ? "1" : "0" })}
                                />
                            </div>

                            {bookingData.is_recurring === "1" && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-blue-100">
                                    <div className="input-container mb-0">
                                        <label className="input-label font-semibold">Recurrence Frequency</label>
                                        <select
                                            className="select-input mb-0"
                                            value={bookingData.recurrence_rule || "weekly_indefinite"}
                                            onChange={(e) => setBookingData({ ...bookingData, recurrence_rule: e.target.value })}
                                        >
                                            <option value="weekly_indefinite">Every week (Forever / 1 Year ahead)</option>
                                            <option value="weekly_count">Every week for specific number of times (X times)</option>
                                        </select>
                                    </div>

                                    {bookingData.recurrence_rule === "weekly_count" && (
                                        <div className="input-container mb-0">
                                            <label className="input-label font-semibold">Number of Times (Occurrences)</label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="52"
                                                className="text-input mb-0"
                                                placeholder="e.g. 10"
                                                value={bookingData.recurrence_count || 10}
                                                onChange={(e) => setBookingData({ ...bookingData, recurrence_count: e.target.value })}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {booking_id !== "new" && (
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 grid grid-cols-1 sm:grid-cols-4 gap-4">
                            <div>
                                <span className="text-xs text-gray-500 font-semibold block">Booking Status</span>
                                <span className="font-bold text-sm text-gray-900 capitalize">{bookingData.status}</span>
                            </div>
                            <div>
                                <span className="text-xs text-gray-500 font-semibold block">Applicant</span>
                                <span className="font-bold text-sm text-gray-900">
                                    {bookingData.user_data ? `${bookingData.user_data.first_name} ${bookingData.user_data.last_name}` : `User #${bookingData.user_id}`}
                                </span>
                            </div>
                            <div>
                                <span className="text-xs text-gray-500 font-semibold block">Approved By</span>
                                <span className="font-bold text-sm text-gray-900">
                                    {bookingData.approver_data ? `${bookingData.approver_data.first_name} ${bookingData.approver_data.last_name}` : "N/A (Auto/Pending)"}
                                </span>
                            </div>
                            <div>
                                <span className="text-xs text-gray-500 font-semibold block">Recurrence</span>
                                <span className="font-bold text-sm text-gray-900">
                                    {bookingData.is_recurring === "1" ? (bookingData.recurrence_rule === "weekly_count" ? `Weekly (${bookingData.recurrence_count}x)` : "Weekly (Forever)") : "One-off"}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </Card>
            </div>
        </AdminLayout>
    );
}
